import { useMemo, useState } from "react"

import { uniqBy } from "lodash-es"
import moment from "moment-timezone"

import { OneVerifyFlagType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import {
    PoliciesListType,
    PolicyAssignability,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"
import {
    IPoliciesDateRangeFilterData,
    IPoliciesListFiltersData,
} from "@common-shared/types/policies.types"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { POLICIES_CONFIG } from "../config/policies.config"
import { PermissionsHelper } from "../helpers/permissions.helper"
import { UtilHelper } from "../helpers/util.helper"
import { useAppDispatch } from "../store/appDispatch.hook"
import { useAppSelector } from "../store/selectors/app.selector"
import { selectedPracticeRoleSelector } from "../store/selectors/selectedPracticeRole.selector"
import { policiesUpdateListItems } from "../store/slices/policies.slice"
import { policiesReloadList } from "../store/thunks/policies.thunks"
import { useBulkOverrides } from "./bulkOverrides.hooks"

export const usePoliciesList = () => {
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const dispatch = useAppDispatch()

    const { isOverridesEnabled } = useBulkOverrides()

    const [selectedPolicies, setSelectedPolicies] = useState<
        IOneVerifyCoverageCheckHistory[]
    >([])

    const canManageOverrides = useMemo<boolean>(
        () =>
            PermissionsHelper.hasPermission(
                OneVerifyPermission.overrideCoverageData,
                selectedPracticeRole,
            ),
        [selectedPracticeRole],
    )

    const canRunCoverageChecks = useMemo<boolean>(
        () =>
            PermissionsHelper.hasPermission(
                OneVerifyPermission.runCoverageChecks,
                selectedPracticeRole,
            ),
        [selectedPracticeRole],
    )

    const isPolicySelected = (
        policy: IOneVerifyCoverageCheckHistory,
    ): boolean => {
        return !!selectedPolicies.find(item => {
            return (
                item.coverageResult.resultId === policy.coverageResult.resultId
            )
        })
    }

    const onPoliciesSelected = (items: IOneVerifyCoverageCheckHistory[]) => {
        setSelectedPolicies(current => {
            return uniqBy([...current, ...items], item => {
                return item.coverageResult.resultId
            })
        })
    }

    const onPoliciesDeselected = (items: IOneVerifyCoverageCheckHistory[]) => {
        setSelectedPolicies(current => {
            const notNeededRequestIds = items.map(
                item => item.coverageResult.resultId,
            )

            return current.filter(
                policy =>
                    !notNeededRequestIds.includes(
                        policy.coverageResult.resultId,
                    ),
            )
        })
    }

    const onDeselectAllPolicies = () => {
        setSelectedPolicies([])
    }

    const processFiltersFormData = (
        data: IPoliciesListFiltersData,
        selectedFlags?: OneVerifyFlagType[],
    ): IPoliciesListFiltersData => {
        let result: IPoliciesListFiltersData = { ...data }

        const dateRanges: (keyof IPoliciesListFiltersData)[] = [
            "checkDate",
            "nextAppointmentDate",
            "planEndDate",
            "overrideExpirationDate",
            "taskAssignmentDateWorked",
            "customFieldAppointmentCreatedTimestamp",
        ]

        // Convert dates to mysql format for date ranges
        dateRanges.forEach(key => {
            const typedDataRange = result[key] as
                | IPoliciesDateRangeFilterData
                | undefined

            result = {
                ...result,

                [key]: typedDataRange?.type
                    ? {
                          ...typedDataRange,

                          dateFrom: typedDataRange.dateFrom
                              ? UtilHelper.dateToMysqlFormat(
                                    moment(typedDataRange.dateFrom).toDate(),
                                )
                              : typedDataRange.dateTo
                                ? UtilHelper.dateToMysqlFormat(
                                      moment(typedDataRange.dateTo)
                                          .subtract(
                                              POLICIES_CONFIG.defaultReportsExtendedGoBackDays,
                                              "days",
                                          )
                                          .toDate(),
                                  )
                                : undefined,

                          dateTo: typedDataRange.dateTo
                              ? UtilHelper.dateToMysqlFormat(
                                    moment(typedDataRange.dateTo).toDate(),
                                )
                              : undefined,

                          // convert to number
                          daysAmount: typedDataRange.daysAmount
                              ? +typedDataRange.daysAmount
                              : undefined,
                      }
                    : undefined,
            }
        })

        const taskAssignmentsUnassigned = result.taskAssignmentsUnassigned
            ? true
            : undefined

        const finalResult: IPoliciesListFiltersData = {
            ...result,

            // Convert date to mysql format for dob
            memberDob: result.memberDob
                ? UtilHelper.dateToMysqlFormat(
                      moment(result.memberDob).toDate(),
                  )
                : undefined,

            flags: selectedFlags,

            taskAssignmentsUnassigned,

            taskAssignmentAssignee:
                !taskAssignmentsUnassigned &&
                result.taskAssignmentAssignability !==
                    PolicyAssignability.UNASSIGNABLE
                    ? result.taskAssignmentAssignee
                    : undefined,

            taskAssignmentAssignability: result.taskAssignmentAssignability
                ? result.taskAssignmentAssignability
                : undefined,

            responseType: result.responseType
                ? Array.isArray(result.responseType)
                    ? result.responseType
                    : [result.responseType]
                : undefined,
        }

        // Remove empty arrays
        Object.keys(finalResult).forEach(key => {
            const typedKey = key as keyof IPoliciesListFiltersData

            if (
                Array.isArray(finalResult[typedKey]) &&
                !finalResult[typedKey].length
            ) {
                delete finalResult[typedKey]
            }
        })

        return finalResult
    }

    const onSuccessReloadList = (
        listType: PoliciesListType,
        results: (IOneVerifyCoverageCheckHistory | undefined)[],
    ) => {
        if (!selectedPracticeRole) {
            return
        }

        // if has at list one undefined result -> reload entire list
        // otherwise just update in memory
        if (results.some(result => !result)) {
            dispatch(
                policiesReloadList({
                    practice: selectedPracticeRole.practice,
                    payload: {
                        policiesViewType: listType,
                    },
                }),
            )
        } else {
            dispatch(
                policiesUpdateListItems({
                    listType,
                    updatedItems: results as IOneVerifyCoverageCheckHistory[],
                }),
            )
        }
    }

    return {
        // vars
        isOverridesEnabled,
        canManageOverrides,
        canRunCoverageChecks,
        // state
        selectedPolicies,
        setSelectedPolicies,
        // functions
        isPolicySelected,
        onPoliciesSelected,
        onPoliciesDeselected,
        onDeselectAllPolicies,
        processFiltersFormData,
        onSuccessReloadList,
    }
}
